/**
 * Combat-round tracker slice.
 *
 * Spec: `.kiro/specs/active-effects-round-tracker/design.md` — Component:
 * `CombatRoundSlice`. Owns round-scale state (isInCombat, roundNumber,
 * actionCounters, activeEffects) and the end-round orchestration.
 */

import { StateCreator } from 'zustand';
import type { ActionCounters, ActiveEffect } from '../../../types/combatRound';
import {
  HALF_ACTION_BUDGET,
  FULL_ACTION_BUDGET,
  REACTION_BUDGET
} from '../../../types/combatRound';

const zeroCounters = (): ActionCounters => ({
  halfActionsUsed: 0,
  fullActionsUsed: 0,
  reactionsUsed: 0,
  freeActionsUsed: 0
});

const BUDGET_BY_KIND: Record<keyof ActionCounters, number | null> = {
  halfActionsUsed: HALF_ACTION_BUDGET,
  fullActionsUsed: FULL_ACTION_BUDGET,
  reactionsUsed: REACTION_BUDGET,
  freeActionsUsed: null
};

// Per design.md § "Synthetic concerns audit": endCombat clears round-scale
// entries but preserves sustained-power entries (psyker keeps the power up
// between combats unless they drop concentration).
const CLEARED_ON_END_COMBAT: ReadonlyArray<ActiveEffect['sourceKind']> = [
  'aim',
  'reload',
  'sprint',
  'temporary-effect',
  'per-round-damage',
  'combat-state-mirror'
];

export interface CombatRoundSlice {
  // State
  isInCombat: boolean;
  roundNumber: number;
  actionCounters: ActionCounters;
  activeEffects: Record<string, ActiveEffect>;

  // Actions
  startCombat: () => void;
  endCombat: () => void;
  endRound: () => void;
  addActiveEffect: (effect: ActiveEffect) => void;
  removeActiveEffect: (id: string) => void;
  incrementActionCounter: (kind: keyof ActionCounters) => void;

  // Selectors
  getActiveEffectsList: () => ActiveEffect[];
  isOverBudget: (kind: keyof ActionCounters) => boolean;
}

export const createCombatRoundSlice: StateCreator<CombatRoundSlice> = (set, get) => ({
  isInCombat: false,
  roundNumber: 0,
  actionCounters: zeroCounters(),
  activeEffects: {},

  startCombat: () => {
    set({ isInCombat: true, roundNumber: 1, actionCounters: zeroCounters() });
  },

  endCombat: () => {
    set((state) => {
      const surviving: Record<string, ActiveEffect> = {};
      for (const [id, e] of Object.entries(state.activeEffects)) {
        if (!CLEARED_ON_END_COMBAT.includes(e.sourceKind)) {
          surviving[id] = e;
        }
      }
      return { isInCombat: false, actionCounters: zeroCounters(), activeEffects: surviving };
    });
  },

  endRound: () => {
    // Tick combat states via existing slice (safe when mounted in same store).
    const full = get() as unknown as {
      tickRound?: () => void;
      setAimBonus?: (n: number) => void;
    };
    full.tickRound?.();

    // Decrement activeEffects; remove zero-crossing entries. Nulls persist.
    set((state) => {
      const next: Record<string, ActiveEffect> = {};
      for (const [id, e] of Object.entries(state.activeEffects)) {
        if (e.roundsRemaining === null) {
          next[id] = e;
        } else if (e.roundsRemaining > 1) {
          next[id] = { ...e, roundsRemaining: e.roundsRemaining - 1 };
        }
        // roundsRemaining <= 1 after decrement → drop
      }
      return {
        activeEffects: next,
        roundNumber: state.roundNumber + 1,
        actionCounters: zeroCounters()
      };
    });

    // Clear aim bonus unconditionally per AC 5.1 + design § Synthetic concerns.
    full.setAimBonus?.(0);
  },

  addActiveEffect: (effect) => {
    set((state) => ({
      activeEffects: { ...state.activeEffects, [effect.id]: effect }
    }));
  },

  removeActiveEffect: (id) => {
    set((state) => {
      if (!(id in state.activeEffects)) return state;
      const { [id]: _removed, ...rest } = state.activeEffects;
      return { activeEffects: rest };
    });
  },

  incrementActionCounter: (kind) => {
    set((state) => ({
      actionCounters: { ...state.actionCounters, [kind]: state.actionCounters[kind] + 1 }
    }));
  },

  getActiveEffectsList: () => {
    return Object.values(get().activeEffects).sort((a, b) => a.createdAt - b.createdAt);
  },

  isOverBudget: (kind) => {
    const budget = BUDGET_BY_KIND[kind];
    if (budget === null) return false;
    return get().actionCounters[kind] > budget;
  }
});

// Re-export budget constants for convenience at the slice boundary.
export { HALF_ACTION_BUDGET, FULL_ACTION_BUDGET, REACTION_BUDGET };
